/**
 * Prediction Watchlist API client.
 *
 * Wraps the backend at /api/v1/watchlist (see
 * backend/app/api/v1/watchlist.py). The Dashboard reads `total` to show
 * the count badge; PredictionWatchlistPage reads the full payload.
 */
import api from './api';

export type WatchlistKind = 'deal' | 'investor' | 'company';

export interface MacroSignal {
  area: string;
  signal: string;
  supports: string;
}

export interface WatchlistEntry {
  id: number;
  kind: WatchlistKind;
  entity_id: number;
  name: string;
  priority: number;
  reason: string;
  macro_signals: MacroSignal[];
  href: string;
  as_of: string | null;
  stale_after: string | null;
  dismissed_at: string | null;
}

export interface WatchlistPayload {
  deals: WatchlistEntry[];
  investors: WatchlistEntry[];
  companies: WatchlistEntry[];
  as_of: string | null;
  macro_summary: string;
  total: number;
}

export interface WatchlistRegenResult {
  regenerated: boolean;
  counts: {
    deal: number;
    investor: number;
    company: number;
  };
}

const watchlistService = {
  async fetchAll(): Promise<WatchlistPayload> {
    const resp = await api.get('/watchlist');
    return resp.data as WatchlistPayload;
  },

  async fetchDeals(): Promise<{ deals: WatchlistEntry[]; total: number }> {
    const resp = await api.get('/watchlist/deals');
    return resp.data;
  },

  async fetchInvestors(): Promise<{ investors: WatchlistEntry[]; total: number }> {
    const resp = await api.get('/watchlist/investors');
    return resp.data;
  },

  async fetchCompanies(): Promise<{ companies: WatchlistEntry[]; total: number }> {
    const resp = await api.get('/watchlist/companies');
    return resp.data;
  },

  async regenerate(): Promise<WatchlistRegenResult> {
    const resp = await api.post('/watchlist/regenerate');
    return resp.data as WatchlistRegenResult;
  },
};

export default watchlistService;
